import React from 'react';
import classNames from 'classnames';

import { Theme, useStyles } from 'ui/base/theme';
import { ButtonClickEvent } from 'ui/base/types';

import { hostDisplayName } from 'services/pangolin/hosts_service';

import {
  themedDeviceTypeIconClassStyles,
  getDeviceTypeIcon,
} from 'assets/enums/device_types/device_types';

import { ALL_DEVICES, DeviceEntry } from './device_entry_logic';

import Messages from './device_entry_messages';
import baseStyles from './device_entry.module.css';

type DeviceEntryStyles = {
  // DeviceEntry
  plainContainer: string;
  clickableContainer: string;
  deviceIconContainer: string;
  deviceIcon: string;
  deviceIconAll: string;
  deviceDetailColumn: string;
  bareButton: string;
  removeIcon: string;

  // EntryDetails
  detailsContainer: string;
  primaryDetail: string;
  secondaryDetail: string;
};

const themedStyles: Record<Theme, DeviceEntryStyles> = {
  [Theme.DAYLIGHT]: {
    // DeviceEntry
    plainContainer: baseStyles.plainContainer,
    clickableContainer: baseStyles.clickableContainer,
    deviceIconContainer: baseStyles.deviceIconContainer,
    deviceIcon: baseStyles.deviceIcon,
    deviceIconAll: baseStyles.daylightDeviceIconAll,
    deviceDetailColumn: baseStyles.deviceDetailColumn,
    bareButton: baseStyles.bareButton,
    removeIcon: baseStyles.daylightRemoveIcon,

    // EntryDetails
    detailsContainer: baseStyles.detailsContainer,
    primaryDetail: baseStyles.primaryDetail,
    secondaryDetail: baseStyles.secondaryDetail,
  },
  [Theme.MIDNIGHT]: {
    // DeviceEntry
    plainContainer: baseStyles.plainContainer,
    clickableContainer: baseStyles.clickableContainer,
    deviceIconContainer: baseStyles.deviceIconContainer,
    deviceIcon: baseStyles.deviceIcon,
    deviceIconAll: baseStyles.midnightDeviceIconAll,
    deviceDetailColumn: baseStyles.deviceDetailColumn,
    bareButton: baseStyles.bareButton,
    removeIcon: baseStyles.midnightRemoveIcon,

    // EntryDetails
    detailsContainer: baseStyles.detailsContainer,
    primaryDetail: baseStyles.primaryDetail,
    secondaryDetail: baseStyles.secondaryDetail,
  },
};

type EntryDetailsProps = {
  styles: DeviceEntryStyles;
  entry: DeviceEntry;
};

export const EntryDetails = ({ styles, entry }: EntryDetailsProps) => {
  if (entry.kind === ALL_DEVICES) {
    return (
      <div className={styles.detailsContainer}>
        <div className={styles.primaryDetail}>{Messages.allDevices()}</div>
      </div>
    );
  } else {
    return (
      <div className={styles.detailsContainer}>
        <div className={styles.primaryDetail}>{hostDisplayName(entry.host)}</div>
        <div className={styles.secondaryDetail}>
          <span>{entry.host.ip || entry.host.mac}</span>
        </div>
      </div>
    );
  }
};

type RemovableDeviceEntryProps = {
  entry: DeviceEntry;
  onRemoveClick: (event: ButtonClickEvent) => void;
};

export const RemovableDeviceEntry = ({ entry, onRemoveClick }: RemovableDeviceEntryProps) => {
  const styles = useStyles(themedStyles);
  const deviceIcons = useStyles(themedDeviceTypeIconClassStyles);
  const deviceIconClass = entry.kind === ALL_DEVICES ? styles.deviceIconAll :
                          getDeviceTypeIcon(deviceIcons, entry.kind);
  return (
    <div className={styles.plainContainer}>
      <div className={styles.deviceIconContainer}>
        <div className={classNames(styles.deviceIcon, deviceIconClass)}/>
      </div>
      <div className={styles.deviceDetailColumn}>
        <EntryDetails styles={styles} entry={entry}/>
        <button type='button' className={styles.bareButton} onClick={onRemoveClick}>
          <div className={styles.removeIcon}/>
        </button>
      </div>
    </div>
  );
};

export type SearchDeviceEntryProps = {
  data: DeviceEntry;
  onSelect: () => void;
};

export const SearchDeviceEntry = ({ data: entry, onSelect }: SearchDeviceEntryProps) => {
  const styles = useStyles(themedStyles);
  const deviceIcons = useStyles(themedDeviceTypeIconClassStyles);
  const deviceIconClass = entry.kind === ALL_DEVICES ? styles.deviceIconAll :
                          getDeviceTypeIcon(deviceIcons, entry.kind);
  return (
    <div className={styles.clickableContainer} onClick={onSelect}>
      <div className={styles.deviceIconContainer}>
        <div className={classNames(styles.deviceIcon, deviceIconClass)}/>
      </div>
      <div className={styles.deviceDetailColumn}>
        <EntryDetails styles={styles} entry={entry}/>
      </div>
    </div>
  );
};

export type PlainDeviceEntryProps = {
  entry: DeviceEntry;
};

export const PlainDeviceEntry = ({ entry }: PlainDeviceEntryProps) => {
  const styles = useStyles(themedStyles);
  const deviceIcons = useStyles(themedDeviceTypeIconClassStyles);
  const deviceIconClass = entry.kind === ALL_DEVICES ? styles.deviceIconAll :
                          getDeviceTypeIcon(deviceIcons, entry.kind);
  return (
    <div className={styles.plainContainer}>
      <div className={styles.deviceIconContainer}>
        <div className={classNames(styles.deviceIcon, deviceIconClass)}/>
      </div>
      <div className={styles.deviceDetailColumn}>
        <EntryDetails styles={styles} entry={entry}/>
      </div>
    </div>
  );
};
